/**
 * Category Data for SEO-Friendly URLs
 * Maps URL slugs to specialization IDs and user types
 */

export interface CategoryInfo {
  name: string;
  slug: string;
  specializationIds: number[];
  userType?: 'creator' | 'model'; // undefined = both
  description: string;
}

export const CATEGORY_DATA: Record<string, CategoryInfo> = {
  // Wedding & Events
  'wedding-photographers': {
    name: 'Wedding Photographers',
    slug: 'wedding-photographers',
    specializationIds: [12], // Wedding Photography (ID: 12)
    userType: 'creator',
    description: 'Professional wedding photographers for your special day'
  },
  'wedding-videographers': {
    name: 'Wedding Videographers',
    slug: 'wedding-videographers',
    specializationIds: [41], // Wedding Videography (ID: 41)
    userType: 'creator',
    description: 'Expert wedding videographers to capture your memories'
  },
  'prewedding-photographers': {
    name: 'Pre-Wedding Photographers',
    slug: 'prewedding-photographers',
    specializationIds: [12, 13], // Wedding + Engagement Photography
    userType: 'creator',
    description: 'Pre-wedding and engagement shoot photographers'
  },
  'engagement-photographers': {
    name: 'Engagement Photographers',
    slug: 'engagement-photographers',
    specializationIds: [13], // Engagement Photography (ID: 13)
    userType: 'creator',
    description: 'Capture your engagement moments beautifully'
  },
  'event-photographers': {
    name: 'Event Photographers',
    slug: 'event-photographers',
    specializationIds: [14], // Event Photography (ID: 14)
    userType: 'creator',
    description: 'Professional event photographers for all occasions'
  },
  'event-videographers': {
    name: 'Event Videographers',
    slug: 'event-videographers',
    specializationIds: [42], // Event Videography (ID: 42)
    userType: 'creator',
    description: 'Event videographers for memorable recordings'
  },
  'birthday-photographers': {
    name: 'Birthday Photographers',
    slug: 'birthday-photographers',
    specializationIds: [17], // Birthday / Party Photography (ID: 17)
    userType: 'creator',
    description: 'Birthday and party photographers'
  },
  
  // Portrait & Fashion
  'portrait-photographers': {
    name: 'Portrait Photographers',
    slug: 'portrait-photographers',
    specializationIds: [1], // Portrait Photography (ID: 1)
    userType: 'creator',
    description: 'Professional portrait photographers'
  },
  'fashion-photographers': {
    name: 'Fashion Photographers',
    slug: 'fashion-photographers',
    specializationIds: [3], // Fashion Photography (ID: 3)
    userType: 'creator',
    description: 'Fashion photographers for editorial and commercial shoots'
  },
  'fashion-models': {
    name: 'Fashion Models',
    slug: 'fashion-models',
    specializationIds: [3, 5], // Fashion Photography + Editorial Photography (Model specializations)
    userType: 'model',
    description: 'Professional fashion models'
  },
  'headshot-photographers': {
    name: 'Headshot Photographers',
    slug: 'headshot-photographers',
    specializationIds: [2], // Headshots (ID: 2)
    userType: 'creator',
    description: 'Professional headshot photographers'
  },
  
  // Commercial
  'commercial-photographers': {
    name: 'Commercial Photographers',
    slug: 'commercial-photographers',
    specializationIds: [22], // Commercial Advertising Photography (ID: 22)
    userType: 'creator',
    description: 'Commercial and advertising photographers'
  },
  'product-photographers': {
    name: 'Product Photographers',
    slug: 'product-photographers',
    specializationIds: [18], // Product Photography (ID: 18)
    userType: 'creator',
    description: 'Product photographers for e-commerce and catalogs'
  },
  'food-photographers': {
    name: 'Food Photographers',
    slug: 'food-photographers',
    specializationIds: [19], // Food Photography (ID: 19)
    userType: 'creator',
    description: 'Food photographers for restaurants and brands'
  },
  'real-estate-photographers': {
    name: 'Real Estate Photographers',
    slug: 'real-estate-photographers',
    specializationIds: [20], // Real Estate Photography (ID: 20)
    userType: 'creator',
    description: 'Real estate and property photographers'
  },
  
  // Videography
  'corporate-videographers': {
    name: 'Corporate Videographers',
    slug: 'corporate-videographers',
    specializationIds: [43, 54], // Corporate Video Production (ID: 43, 54)
    userType: 'creator',
    description: 'Corporate video production specialists'
  },
  'music-video-directors': {
    name: 'Music Video Directors',
    slug: 'music-video-directors',
    specializationIds: [45], // Music Video Production (ID: 45)
    userType: 'creator',
    description: 'Music video directors and producers'
  },
  'drone-videographers': {
    name: 'Drone Videographers',
    slug: 'drone-videographers',
    specializationIds: [48, 34], // Drone Videography + Aerial Photography
    userType: 'creator',
    description: 'Aerial and drone videographers'
  },
  
  // Lifestyle & Family
  'lifestyle-photographers': {
    name: 'Lifestyle Photographers',
    slug: 'lifestyle-photographers',
    specializationIds: [7], // Lifestyle Photography (ID: 7)
    userType: 'creator',
    description: 'Lifestyle photographers for natural, candid shots'
  },
  'family-photographers': {
    name: 'Family Photographers',
    slug: 'family-photographers',
    specializationIds: [8], // Family Photography (ID: 8)
    userType: 'creator',
    description: 'Family portrait photographers'
  },
  'maternity-photographers': {
    name: 'Maternity Photographers',
    slug: 'maternity-photographers',
    specializationIds: [9], // Maternity Photography (ID: 9)
    userType: 'creator',
    description: 'Maternity and pregnancy photographers'
  },
  'newborn-photographers': {
    name: 'Newborn Photographers',
    slug: 'newborn-photographers',
    specializationIds: [10], // Newborn Photography (ID: 10)
    userType: 'creator',
    description: 'Newborn and baby photographers'
  },
  
  // Model Categories - Using relevant photography skills that models would have
  'fitness-models': {
    name: 'Fitness Models',
    slug: 'fitness-models',
    specializationIds: [94], // Fitness (ID: 94)
    userType: 'model',
    description: 'Professional fitness models'
  },
  'commercial-models': {
    name: 'Commercial Models',
    slug: 'commercial-models',
    specializationIds: [93], // Commercial (ID: 93)
    userType: 'model',
    description: 'Commercial and advertising models'
  },
  'runway-models': {
    name: 'Runway Models',
    slug: 'runway-models',
    specializationIds: [96], // Runway (ID: 96)
    userType: 'model',
    description: 'Runway and fashion week models'
  },
  'beauty-models': {
    name: 'Beauty Models',
    slug: 'beauty-models',
    specializationIds: [95, 4], // Beauty (ID: 95, 4)
    userType: 'model',
    description: 'Beauty and cosmetic shoot models'
  },
  'editorial-models': {
    name: 'Editorial Models',
    slug: 'editorial-models',
    specializationIds: [98, 5], // Editorial (ID: 98, 5)
    userType: 'model',
    description: 'Editorial and magazine models'
  },
  
  // Specialty
  'travel-photographers': {
    name: 'Travel Photographers',
    slug: 'travel-photographers',
    specializationIds: [30], // Travel Photography (ID: 30)
    userType: 'creator',
    description: 'Travel and destination photographers'
  },
  'wildlife-photographers': {
    name: 'Wildlife Photographers',
    slug: 'wildlife-photographers',
    specializationIds: [31], // Wildlife Photography (ID: 31)
    userType: 'creator',
    description: 'Wildlife and nature photographers'
  },
  'fine-art-photographers': {
    name: 'Fine Art Photographers',
    slug: 'fine-art-photographers',
    specializationIds: [24], // Fine Art Photography (ID: 24)
    userType: 'creator',
    description: 'Fine art and conceptual photographers'
  },
  
  // Broad Directory Queries
  'models': {
    name: 'Models',
    slug: 'models',
    specializationIds: [],
    userType: 'model',
    description: 'Connect with professional models, fashion models, commercial models, runway models, and creative talent'
  },
  'photographers': {
    name: 'Photographers',
    slug: 'photographers',
    specializationIds: [1, 2, 3, 7, 8, 9, 10, 12, 13, 14, 17, 18, 19, 20, 22, 24, 30, 31],
    userType: 'creator',
    description: 'Find professional photographers, wedding photographers, portrait photographers, fashion photographers, and commercial photography experts'
  },
  'videographers': {
    name: 'Videographers',
    slug: 'videographers',
    specializationIds: [34, 41, 42, 43, 45, 48, 54],
    userType: 'creator',
    description: 'Find professional videographers, wedding videographers, event videographers, corporate video producers, and drone videography experts'
  },
};

// Helper function to get category by slug
export const getCategoryBySlug = (slug: string): CategoryInfo | undefined => {
  return CATEGORY_DATA[slug.toLowerCase()];
};

// Get all category names for display
export const getAllCategoryNames = (): string[] => {
  return Object.values(CATEGORY_DATA).map(cat => cat.name);
};

// Get all category slugs
export const getAllCategorySlugs = (): string[] => {
  return Object.keys(CATEGORY_DATA);
};

// Get categories by user type
export const getCategoriesByUserType = (userType: 'creator' | 'model'): CategoryInfo[] => {
  return Object.values(CATEGORY_DATA).filter(cat => cat.userType === userType || !cat.userType);
};
